import { DateTime } from "luxon";
import YAML from "yaml";

import { TIMEZONE } from "@/common/constants";
import { deliveryOptionsCodes } from "@/common/fields/delivery-options";
import { paymentMethodsCodesReader } from "@/common/fields/orders-payment-methods";
import { paymentStatusesCodes } from "@/common/fields/orders-payment-status-codes";
import { ordersStatusesCodes } from "@/common/fields/orders-status-codes";
import { handleErrorAxios } from "@/common/lib/formatter";
import Http from "@/common/lib/http";
import { formatReference } from "@/common/lib/reference";
import { applyFormatOrder } from "@/common/lib/transformers";

const _getDate = (date) =>
    DateTime.fromJSDate(new Date(date), {
        zone: TIMEZONE
    }).toFormat("yyyy-MM-dd hh:mm");

const _formatDate = (managementDates) => {
    return managementDates
        ? managementDates.map((item) => _getDate(item.value))
        : [];
};

const actions = {
    async listOrdersByCustomerAndPage(
        { commit, dispatch },
        { customerId, page }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/orders", {
                params: { page, customerId, deliveryStatus: "completed" }
            });
            const items = data.items.map(applyFormatOrder);
            return { ...data, items };
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async listOrdersOwnPaginated(
        { commit, dispatch },
        {
            complete,
            createdAt,
            customerId,
            deliveryAt,
            deliveryOption,
            deliveryStatus,
            end,
            id,
            merchantId,
            merchantType,
            page,
            paymentStatus,
            sellerId,
            uuid
        }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/orders/own", {
                params: {
                    complete,
                    createdAt,
                    customerId,
                    deliveryAt,
                    deliveryOption,
                    deliveryStatus,
                    end,
                    id,
                    merchantId,
                    merchantType,
                    page,
                    paymentStatus,
                    sellerId,
                    uuid
                }
            });
            const items = data.items.map(applyFormatOrder);
            return { ...data, items };
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async listOrdersPaginated(
        { commit, dispatch },
        {
            complete,
            createdAt,
            customerId,
            deliveryAt,
            deliveryOption,
            deliveryStatus,
            id,
            itemsByPage,
            merchantId,
            merchantType,
            origin,
            page,
            paymentStatus,
            sellerId,
            uuid
        }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/orders", {
                params: {
                    complete,
                    createdAt,
                    customerId,
                    deliveryAt,
                    deliveryOption,
                    deliveryStatus,
                    id,
                    itemsByPage,
                    merchantId,
                    merchantType,
                    origin,
                    page,
                    paymentStatus,
                    sellerId,
                    uuid
                }
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async listOrdersSchedule(
        { commit, dispatch },
        {
            begin,
            customerId,
            deliveryOption,
            deliveryStatus,
            end,
            forwardDays,
            forwardMonths,
            merchantId,
            merchantType,
            origin,
            paymentStatus,
            sellerId,
            uuid
        }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/orders/schedule", {
                params: {
                    deliveryStatus,
                    deliveryOption,
                    paymentStatus,
                    customerId,
                    sellerId,
                    merchantType,
                    merchantId,
                    uuid,
                    origin,
                    begin,
                    end,
                    forwardDays,
                    forwardMonths
                }
            });
            const items = data.items.map(applyFormatOrder);
            return { ...data, items };
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async listAllOrders(
        { commit, dispatch },
        {
            createdAt,
            customerId,
            deliveryAt,
            deliveryOption,
            deliveryStatus,
            id,
            merchantId,
            merchantType,
            origin,
            paymentStatus,
            sellerId,
            uuid
        }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/orders/all", {
                params: {
                    createdAt,
                    customerId,
                    deliveryAt,
                    deliveryOption,
                    deliveryStatus,
                    id,
                    merchantId,
                    merchantType,
                    origin,
                    paymentStatus,
                    sellerId,
                    uuid
                }
            });
            const orders = data.items.map((item) => ({
                id: item.id,
                uuid: item.uuid,
                createdAt: _getDate(item.createdAt),
                updatedAt: _getDate(item.updatedAt),
                deliveryAt: _getDate(item.deliveryAt),
                paymentAt: _getDate(item.paymentAt),
                deliveryStatusMessage:
                    ordersStatusesCodes[item.deliveryStatus] ||
                    ordersStatusesCodes.pending,
                paymentStatusMessage:
                    paymentStatusesCodes[item.paymentStatus] ||
                    paymentStatusesCodes.pending,
                deliveryOptionMessage:
                    deliveryOptionsCodes[item.deliveryOption] ||
                    deliveryOptionsCodes.home,
                paymentMethodMessage:
                    paymentMethodsCodesReader[item.paymentMethod] ||
                    paymentMethodsCodesReader.cash,
                paymentTransactionRef: item.paymentTransactionRef,
                origin: item.origin,
                total: item.total,
                tax: item.tax,
                valuePaid: item.valuePaid,
                totalDiscountApplied: item.totalDiscountApplied,
                stockDecremented: item.stockDecremented,
                supplyMerchant: item.supplyMerchant
                    ? `${item.supplyMerchant.merchantType}#${item.supplyMerchant.merchantId}`
                    : "",
                pickupMerchant: item.pickupMerchant
                    ? `${item.pickupMerchant.merchantType}#${item.pickupMerchant.merchantId}`
                    : "",
                location: item.location ? YAML.stringify(item.location) : "",
                references: YAML.stringify([
                    ...item.references.map((item2) => ({
                        id: item2.id,
                        barcode: item2.barcode,
                        name: item2.product.name,
                        quantity: item2.quantity,
                        price: item2.priceOffer
                    })),
                    ...item.anonymousReferences.map((item2, index) => ({
                        id: item2.id || index,
                        barcode: `DUMMY${index}`,
                        name: item2.product.name,
                        size: item2.size,
                        quantity: item2.quantity,
                        price: item2.priceOffer
                    }))
                ])
            }));
            return orders;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async listNumberOrdersByReferences(
        { commit, dispatch },
        {
            createdAt,
            customerId,
            deliveryAt,
            deliveryOption,
            deliveryStatus,
            merchantId,
            merchantType,
            origin,
            paymentStatus,
            sellerId
        }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/orders/references", {
                params: {
                    createdAt,
                    customerId,
                    deliveryAt,
                    deliveryOption,
                    deliveryStatus,
                    merchantId,
                    merchantType,
                    origin,
                    paymentStatus,
                    sellerId
                }
            });

            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async consultancyOrder({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.get(
                `/bo/orders/consultancy/${id}`
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async getOrderOwnById(
        { commit, dispatch },
        { id, merchantId, merchantType }
    ) {
        try {
            const { data } = await Http.instance.get(
                `/bo/orders/${merchantType}/${merchantId}/${id}`
            );

            const result = {
                ...data,
                ...{
                    ...(data.deliveryAt
                        ? { deliveryAt: data.deliveryAt }
                        : null)
                },
                references: data.references.map(formatReference),
                anonymousReferences: data.anonymousReferences.map(
                    (item, index) => ({
                        ...item,
                        anonymousId: index
                    })
                )
            };
            return result;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async getOrderById({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.get(`/bo/orders/${id}`);

            const result = {
                ...data,
                ...{
                    ...(data.deliveryAt
                        ? { deliveryAt: data.deliveryAt }
                        : null)
                },
                references: data.references.map(formatReference)
            };
            return result;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async updateOrder(
        { commit, dispatch, state },
        {
            alterInventory,
            anonymousReferences,
            customerId,
            customerLocationId,
            deliveryAt,
            deliveryOption,
            deliveryPrice,
            deliveryStatus,
            id,
            location,
            observation,
            paymentMethod,
            paymentStatus,
            references,
            stockDecremented,
            valuePaid
        }
    ) {
        try {
            const { data } = await Http.instance.patch(`/bo/orders/${id}`, {
                alterInventory,
                valuePaid,
                observation,
                deliveryStatus,
                deliveryOption,
                paymentStatus,
                paymentMethod,
                customerLocationId,
                deliveryAt,
                deliveryPrice,
                location,
                references,
                stockDecremented,
                anonymousReferences,
                customerId
            });

            let orders = [...state.orders];
            const index = orders.findIndex((item) => item.id === id);
            if (index) {
                orders[index] = {
                    ...orders[index],
                    ...{
                        ...(paymentStatus
                            ? {
                                  paymentStatus
                              }
                            : null)
                    },
                    ...{
                        ...(deliveryStatus
                            ? {
                                  deliveryStatus
                              }
                            : null)
                    }
                };
                commit("SET_ORDERS", orders);
            }
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async assignOrder(
        { commit, dispatch },
        { id, merchantId, merchantType, validateStock }
    ) {
        try {
            const { data } = await Http.instance.post("/bo/orders/assign", {
                id,
                merchantId,
                merchantType,
                validateStock
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async completeOrder(
        { commit, dispatch },
        { id, verifyAndDecreaseInventory }
    ) {
        try {
            const { data } = await Http.instance.post(
                `/bo/orders/${id}/complete`,
                {
                    verifyAndDecreaseInventory
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async nextOrderStatusesOrder({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.post(`/bo/orders/${id}/next`, {
                id
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async deleteOrder({ commit, dispatch, state }, { id }) {
        try {
            await Http.instance.delete(`/bo/orders/${id}`);
            const orders = [...state.orders];
            const index = orders.findIndex((item) => item.id === id);
            if (index) {
                orders.splice(index, 1);
                commit("SET_ORDERS", orders);
            }
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async refactorOrder({ commit, dispatch }, { id, orders }) {
        try {
            const { data } = await Http.instance.post("/bo/orders/refactor", {
                id,
                orders
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async suggestOrderDeliveryPrice(
        { commit, dispatch },
        { anonymousReferences, cityCode, references }
    ) {
        try {
            const { data } = await Http.instance.post(
                "/bo/orders/delivery-price",
                { anonymousReferences, cityCode, references }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async createOrder(
        { commit, dispatch },
        {
            anonymousReferences,
            customerId,
            customerLocationId,
            deliveryAt,
            deliveryOption,
            deliveryPrice,
            isQuote,
            location,
            merchantId,
            merchantType,
            observation,
            paymentMethod,
            references,
            sellerId,
            sourcePriceMerchant,
            valuePaid,
            verifyAndDecreaseInventory
        }
    ) {
        try {
            const { data } = await Http.instance.post("/bo/orders", {
                anonymousReferences,
                customerId,
                customerLocationId,
                deliveryAt,
                deliveryOption,
                deliveryPrice,
                isQuote,
                location,
                merchantId,
                merchantType,
                observation,
                paymentMethod,
                references,
                sellerId,
                sourcePriceMerchant,
                valuePaid,
                verifyAndDecreaseInventory
            });
            commit("commons/UPDATE_MERCHANTS_GLOBAL", data.merchant, {
                root: true
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async listOrdersByProduct({ commit, dispatch }, { idSku, query }) {
        try {
            const { data } = await Http.instance.get("/bo/orders/product", {
                params: { idSku, ...query }
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async listOrderCompletePaginated({ commit, dispatch }, { createdAt }) {
        try {
            const { data } = await Http.instance.get("/bo/orders/complete", {
                params: {
                    createdAt
                }
            });
            const items = data.items.map((item) => ({
                ...item,
                deliveryStatusMessage:
                    ordersStatusesCodes[item.deliveryStatus] ||
                    ordersStatusesCodes.pending,
                paymentStatusMessage:
                    paymentStatusesCodes[item.paymentStatus] ||
                    paymentStatusesCodes.pending,
                deliveryOptionMessage:
                    deliveryOptionsCodes[item.deliveryOption] ||
                    deliveryOptionsCodes.home,
                paymentMethodMessage:
                    paymentMethodsCodesReader[item.paymentMethod] ||
                    paymentMethodsCodesReader.cash,
                managementDates: _formatDate(item.managementDates)
            }));
            return { ...data, items };
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },

    async listOrdersGroupsPaginated({ commit, dispatch }, { query }) {
        try {
            const { data } = await Http.instance.get("/bo/orders-groups", {
                params: query
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    },
    async getOrderGroupById({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.get(`/bo/orders-groups/${id}`);
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_ORDERS", parseError);
            throw parseError;
        }
    }
};

export default actions;
